import { useEffect, useRef } from 'react';
import { readCartIntent, clearCartIntent, TTL_MS } from 'utils/cartIntentCapture';
import UserUtils from 'utils/User';
import promoUtils from 'utils/Promos';
import anaUtils from 'analytics/utils';
import store from 'store/Store';
import authenticationUtils from 'utils/Authentication';

// SIM-28 (ACS-4473): on /basket mount after successful auth, detect a persisted cart intent
// and replay the triggering action (promo apply/remove, checkout init) via existing functions.
// ADR-TS-016 (scoped exception to ADR-TS-007 -- no general auto-replay).

function isPromoAlreadyApplied(code, appliedPromotions) {
    if (!appliedPromotions || !Array.isArray(appliedPromotions)) {
        return false;
    }

    return appliedPromotions.some(
        promo => promo.couponCode?.toLowerCase() === code?.toLowerCase() || promo.promoCode?.toLowerCase() === code?.toLowerCase()
    );
}

// Analytics taxonomy (CHG-16) is proposed, not finalized. Fire via the repo's real Tag Manager
// seam (anaUtils.fireEventForTagManager -> window CustomEvent) rather than a hand-rolled
// digitalData.event push, so Launch rules can observe these events consistently with the rest of
// the app. Wrapped defensively -- analytics must never block or break the replay flow.
function fireAnalytics(eventName, payload) {
    try {
        anaUtils.fireEventForTagManager(eventName, { detail: payload });
    } catch (e) {
        // analytics must never block the replay flow
    }
}

async function dispatchReplay(intent, appliedPromotions) {
    const { type, params } = intent;

    fireAnalytics('cart_intent_replay_attempted', { type });

    switch (type) {
        case 'applyPromo': {
            if (isPromoAlreadyApplied(params.code, appliedPromotions)) {
                fireAnalytics('cart_intent_replay_result', { type, result: 'already_applied' });

                return;
            }

            await promoUtils.applyPromo(params.code, null, params.ctaType || 'TEXT');
            fireAnalytics('cart_intent_replay_result', { type, result: 'success' });

            break;
        }

        case 'removePromo': {
            if (!isPromoAlreadyApplied(params.code, appliedPromotions)) {
                // Promo is not on the basket -> nothing to remove (idempotent no-op).
                fireAnalytics('cart_intent_replay_result', { type, result: 'already_removed' });

                return;
            }

            await promoUtils.removePromo(params.code, params.ctaType || 'TEXT');
            fireAnalytics('cart_intent_replay_result', { type, result: 'success' });

            break;
        }

        case 'checkout': {
            const RwdBasketActions = (await import('actions/RwdBasketActions/RwdBasketActions')).default;
            // initiateCheckout is a redux-thunk (returns `async dispatch => {...}`) — it must be
            // DISPATCHED, exactly as the Checkout button's click handler does
            // (CheckoutButton.f.jsx: `dispatch(initiateCheckout(isBopis))`). Calling it bare would
            // just return the thunk and never run it (no API call, no navigation to /checkout).
            // On success the thunk's initOrderSuccess navigates the user to the checkout page, so
            // replaying it reproduces the user's original click end-to-end.
            store.dispatch(RwdBasketActions.initiateCheckout(params.isBopis || false));
            // The thunk handles its own success/failure internally and does not signal back here,
            // so report 'dispatched' (not 'success') to avoid misleading telemetry (ACS-4473 review).
            fireAnalytics('cart_intent_replay_result', { type, result: 'dispatched' });

            break;
        }

        default:
            break;
    }
}

/**
 * SIM-28 (ACS-4473): replay a cart-originated sign-in trigger after successful auth.
 * On basket mount (once basket data is loaded), reads the one-shot persisted intent and, if it is
 * fresh (within TTL) and the user is signed in, replays the original action via existing functions.
 * If the user is not signed in (abandoned sign-in) the intent is consumed and NOT replayed
 * (ADR-TS-016 safe default). Runs at most once per mount lifecycle.
 *
 * @param {boolean} basketLoaded - true once cart data is available (idempotency needs it).
 * @param {Array<Object>} appliedPromotions - current applied promotions (for the idempotency guard).
 * @returns {void}
 */
export default function useCartIntentReplay(basketLoaded, appliedPromotions) {
    const hasReplayed = useRef(false);

    useEffect(() => {
        if (!basketLoaded || hasReplayed.current) {
            return;
        }

        // Killswitch guard (ACS-4473 review): only replay when the modernized sign-in flow is ON.
        // If it's OFF, drop any stale intent (from a prior cohort/session or other caller) without
        // replaying so a flag flip can't resurrect an old cart action.
        if (!authenticationUtils.isNewSignInFlowEnabled()) {
            clearCartIntent();

            return;
        }

        const intent = readCartIntent();

        // Guard against malformed/tampered intents: a missing numeric `ts` would make the TTL
        // check NaN (which is never > TTL_MS), silently bypassing expiry; a missing `type` has no
        // replay branch. Treat either as no-op (intent already cleared one-shot by readCartIntent).
        if (!intent || typeof intent.ts !== 'number' || typeof intent.type !== 'string') {
            return;
        }

        if (Date.now() - intent.ts > TTL_MS) {
            fireAnalytics('cart_intent_expired', { type: intent.type, ageMs: Date.now() - intent.ts });

            return;
        }

        if (!UserUtils.isSignedIn()) {
            fireAnalytics('cart_intent_abandoned', { type: intent.type });

            return;
        }

        hasReplayed.current = true;
        // CR-T-11: replay may reject (e.g. promo service error); catch so it never becomes an
        // unhandled rejection -- promoUtils surfaces the error via its own existing patterns.
        dispatchReplay(intent, appliedPromotions).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basketLoaded]);
}
